import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { notificationService } from "../../services/NotificationService";
import { Notification } from "../../interfaces";

interface NotificationState {
  notifications: Notification[];
  error?: string | null;
  loading: boolean;
}

const initialState: NotificationState = {
  notifications: [],
  error: null,
  loading: false,
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async () => {
    try {
      const response = await notificationService.get();
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const addNotification = createAsyncThunk(
  "notifications/addNotification",
  async (data: any) => {
    try {
      const response = await notificationService.post(data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const updateNotification = createAsyncThunk(
  "notifications/updateNotification",
  async (data: any) => {
    try {
      const response = await notificationService.put(data.id, data);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const getNotificationsByUser = createAsyncThunk(
  "notifications/getNotificationsByUser",
  async (userId: number | string) => {
    try {
      const response = await notificationService.getByUser(userId);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

export const removeNotification = createAsyncThunk(
  "notifications/removeNotification",
  async (id: number) => {
    try {
      const response = await notificationService.remove(id);
      return response.data;
    } catch (error) {
      return error;
    }
  }
);

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(addNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications.push(action.payload);
      })
      .addCase(addNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(updateNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = state.notifications.map((notification) =>
          notification.id === action.payload.id ? action.payload : notification
        );
      })
      .addCase(updateNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(getNotificationsByUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getNotificationsByUser.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(getNotificationsByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(removeNotification.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeNotification.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = state.notifications.filter(
          (notification) => notification.id !== action.payload.id
        );
      })
      .addCase(removeNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

const { reducer } = notificationSlice;
export default reducer;
